import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { datePickerValidator } from '@app/core/Directives/datepicker-validator';
import {
  AccountEntity,
  GroupNames,
  MaxLength,
  ModuleName,
  Modules,
  NotificationTextMessage,
} from '@app/core/Enum';
import {
  Currency,
  GlobalComponent,
  GroupListModel,
  SideListModel,
} from '@app/core/Models';
import { CommonService } from '@app/core/Services';
import { HighlightRow } from '@app/core/Services/common/highlighted-texts.service';
import {
  AccountState,
  CommonState,
  CreateFund,
  CreateAccount,
  GetCurrencyList,
  GetDataByAccountId,
  GetFundNameList,
  GetGroupAccountsBasedOnGroupId,
  GetNonStandardAccountList,
  GetDataByFundId,
  CreateContact,
} from '@app/core/Store';
import { QuickAddComponent } from '@app/modules/common';
import { Select, Store } from '@ngxs/store';
import { Guid } from 'guid-typescript';
import { forkJoin, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-add-debit-note',
  templateUrl: './add-debit-note.component.html',
  styleUrls: ['./add-debit-note.component.scss'],
})
export class AddDebitNoteComponent implements OnInit {
  debitNoteForm: FormGroup;

  currencyList: Currency[];
  supplierList: SideListModel[];
  debitNoteTypeList: GroupListModel[];
  tempDebitNoteTypeList: GroupListModel[];

  @Input() triggereDebitNoteEditData: Observable<any>;

  @Output()
  readonly triggerSupplierChange = new EventEmitter<any>();

  @Output()
  readonly isVatIncludedClick = new EventEmitter<any>();

  @Select(CommonState.defaultCurrency)
  defaultCurrency$: Observable<Guid>;

  getListId: Guid;
  defaultCurrency: Guid;
  fundNameList: any;
  periodicDate: any;
  isVatInclude = false;
  isVatRegistered: any;

  disabledFundEditButton = true;
  disabledEditButton = true;
  isAddMode = true;

  notificationMessage = NotificationTextMessage;
  maxLength = MaxLength;

  private destroy$ = new Subject<void>();

  @Input() isFromBankImport: boolean;
  isSupplierChangePermission: boolean = true;
  isFundChangePermission: boolean = true;

  minInvoiceDate: Date;
  maxInvoiceDate: Date = new Date();

  @Output() fundChanged = new EventEmitter<any>();
  constructor(
    private store: Store,
    public commonService: CommonService,
    public highlightRow: HighlightRow,
    private globalComponent: GlobalComponent,
    private renderer: Renderer2,
    public dialog: MatDialog,
    public datepipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.isVatRegistered = this.globalComponent.getIsVatRegistered();
    this.periodicDate = this.globalComponent.getFinancialPeriod();
    this.setForm();

    this.triggereDebitNoteEditData
      ?.pipe(debounceTime(700), takeUntil(this.destroy$))
      .subscribe((data) => {
        this.isAddMode = false;
        this.editDebitNote(data);
      });

    this.debitNoteForm?.valueChanges?.subscribe((value) => {
      this.commonService.isInitialValueChange = this.debitNoteForm.touched;
    });
    this.setInvoiceDateLimits();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.isSupplierChangePermission = this.commonService.checkPermission(
        Modules.Contacts,
        Modules.Suppliers
      );
      this.isFundChangePermission = this.commonService.checkPermission(
        Modules.Funds,
        Modules.FundList
      );
    }, 3000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  dateChange(): void {
    let startDate: any = new Date(
      this.debitNoteForm.controls['entryDate'].value
    );

    startDate = this.datepipe.transform(startDate, 'yyyy-MM-dd')?.toString();
  }

  loadDropdownValues(): void {
    const groupNames = new Array<number>();
    groupNames.push(GroupNames.Expense);

    const entityId = new Array<number>();
    entityId.push(AccountEntity.Supplier);

    this.defaultCurrency = this.globalComponent.getDefaultCurrency();

    const getCustomer = this.store.dispatch(
      new GetNonStandardAccountList(entityId)
    );
    const getCurrency = this.store.dispatch(new GetCurrencyList());
    const getActivity = this.store.dispatch(
      new GetGroupAccountsBasedOnGroupId(groupNames)
    );
    const getFundNameList = this.store.dispatch(new GetFundNameList());

    forkJoin([
      getCustomer,
      getCurrency,
      getActivity,
      getFundNameList,
    ]).subscribe((res) => {
      this.supplierList = this.store.selectSnapshot(CommonState.accountList);
      this.currencyList = this.store.selectSnapshot(CommonState.getCurrency);
      this.fundNameList = this.store.selectSnapshot(
        AccountState.getFundNameList
      );
      const accountGroupList = this.store.selectSnapshot(
        CommonState.accountGroupList
      );
      if (accountGroupList.length > 0) {
        this.debitNoteTypeList = accountGroupList;
        this.tempDebitNoteTypeList = this.debitNoteTypeList;
      }

      if (this.fundNameList.length > 0) {
        this.disabledFundEditButton = false;
        this.debitNoteForm.controls.fundName.setValue(this.fundNameList[0].id);
        this.fundChanged.emit(this.fundNameList[0].id);
      }

      this.debitNoteForm.controls.currency.setValue(this.defaultCurrency);
      if (this.supplierList.length > 0 && this.isAddMode) {
        this.onSupplierChange();
      }
    });
  }

  gotoAddSupplier(): void {
    this.dialog
      .open(QuickAddComponent, {
        data: {
          moduleId: Modules.Suppliers,
          headerText: `Add ${ModuleName.Suppliers}`,
          saveActionName: CreateContact,
        },
      })
      .afterClosed()
      .subscribe((id) => {
        if (!this.commonService.isEmpty(id)) {
          this.disabledEditButton = false;
          this.getSupplier(id);
        }
      });
  }

  getOptionText(option) {
    return option.name;
  }

  resetAccountList(): void {
    this.debitNoteTypeList = this.tempDebitNoteTypeList;
    this.scrollIntoView(this.debitNoteForm.controls.debitNoteType.value);
  }

  onAccountSearch(event: any): void {
    const list = this.commonService.onAccountSearch(
      event,
      this.tempDebitNoteTypeList
    );
    this.debitNoteTypeList = list;
  }

  gotoEditSupplier(): void {
    if (this.supplierList.length > 0) {
      this.dialog
        .open(QuickAddComponent, {
          data: {
            moduleId: Modules.Suppliers,
            headerText: `Edit ${ModuleName.Suppliers}`,
            id: this.debitNoteForm.controls.accountId.value,
            saveActionName: CreateContact,
            getActionName: GetDataByAccountId,
          },
        })
        .afterClosed()
        .subscribe((id) => {
          if (!this.commonService.isEmpty(id)) {
            this.getSupplier(id);
          }
        });
    }
  }

  getSupplier(id?: Guid): void {
    const entityId = new Array<number>();
    entityId.push(AccountEntity.Supplier);

    this.store
      .dispatch(new GetNonStandardAccountList(entityId))
      .pipe(
        tap((res) => {
          this.supplierList = res.common.accountList;

          if (id !== null && id !== undefined) {
            this.debitNoteForm.controls.accountId.setValue(id);
          }
        })
      )
      .subscribe();
  }

  editDebitNote(data): void {
    let account;
    this.debitNoteTypeList.forEach((items) => {
      const accountId = items.listModels.find(
        (x: any) => x.id === data.bookAccountId
      );
      if (accountId !== null && accountId !== undefined) {
        account = accountId;
      }
    });
    this.debitNoteForm.controls.debitNoteNo.disable();
    this.disabledEditButton = false;
    this.disabledFundEditButton = false;
    this.debitNoteForm.patchValue({
      accountId: data.accountId,
      fundName: data.fundId,
      debitNoteNo: data.entryNumber,
      postingAccountId: data.postingAccountId,
      billRefNo: data.voucherNumber,
      entryDate: data.entryDate,
      currency: data.currencyId,
      note: data.note,
      debitNoteType: account,
      isVatInclude: data.isVatIncluded,
    });
    this.onSupplierChange();
  }

  getSelectedData(item: any[]): void {
    this.debitNoteForm.controls.referenceId.setValue(item);
  }

  setForm(): void {
    this.debitNoteForm = new FormGroup({
      accountId: new FormControl('', [
        Validators.required,
        this.commonService.whiteSpaceValidate,
      ]),
      debitNoteNo: new FormControl('', [
        Validators.required,
        this.commonService.whiteSpaceValidate,
      ]),
      billRefNo: new FormControl(''),
      debitNoteDate: new FormControl(),
      entryDate: new FormControl(
        new Date(),
        datePickerValidator(this.periodicDate)
      ),
      note: new FormControl(''),
      currency: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        this.commonService.whiteSpaceValidate,
      ]),
      debitNoteType: new FormControl('', [Validators.required]),
      fundName: new FormControl('', [
        Validators.required,
        this.commonService.whiteSpaceValidate,
      ]),
      amountReceived: new FormControl(),
      isVatInclude: new FormControl(false),
    });

    this.loadDropdownValues();
  }

  fundChange(event: any): void {
    if (event !== null && event !== undefined) {
      this.disabledFundEditButton = false;
    } else {
      this.disabledFundEditButton = true;
    }
    this.fundChanged.emit(event);
  }

  gotoAddFund(): void {
    this.dialog
      .open(QuickAddComponent, {
        data: {
          moduleId: Modules.FundList,
          headerText: `Add ${ModuleName.Fund}`,
          saveActionName: CreateFund,
        },
      })
      .afterClosed()
      .subscribe((id) => {
        if (!this.commonService.isEmpty(id)) {
          this.disabledFundEditButton = false;
          this.getFund(id);
        }
      });
  }

  getFund(id): void {
    this.store.dispatch(new GetFundNameList()).subscribe((res) => {
      this.fundNameList = res.account.fundNameList;
      if (id !== null && id !== undefined) {
        this.debitNoteForm.controls.fundName.setValue(id);
        this.fundChanged.emit(id);
      }
    });
  }

  gotoEditFund(): void {
    if (this.fundNameList.length > 0) {
      this.dialog
        .open(QuickAddComponent, {
          data: {
            moduleId: Modules.FundList,
            headerText: `Edit ${ModuleName.Fund}`,
            id: this.debitNoteForm.controls.fundName.value,
            saveActionName: CreateFund,
            getActionName: GetDataByFundId,
          },
        })
        .afterClosed()
        .subscribe((id) => {
          if (!this.commonService.isEmpty(id)) {
            this.getFund(id);
          }
        });
    }
  }
  supplierChange(event: any): void {
    if (event !== null && event !== undefined) {
      this.disabledEditButton = false;
    } else {
      this.disabledEditButton = true;
    }
  }

  onSupplierChange(): void {
    this.triggerSupplierChange.emit();
  }

  scrollIntoView(element) {
    if (element !== '') {
      this.commonService.autoScrollMatAutoComplete(this.renderer);
    }
  }

  onCheckBoxSelected(event: any): void {
    this.isVatInclude = event.checked;
    this.isVatIncludedClick.emit(this.isVatInclude);
  }

  setInvoiceDateLimits(): void {
    if (this.periodicDate?.length > 0) {
      const unlockedPeriod = this.periodicDate.find(
        (period: any) => !period.isLocked
      );

      if (unlockedPeriod) {
        this.minInvoiceDate = new Date(unlockedPeriod.fromDate);
      } else {
        this.minInvoiceDate = new Date(this.periodicDate[0].fromDate);
      }

      this.maxInvoiceDate = new Date(
        this.periodicDate[this.periodicDate.length - 1]?.toDate
      );
    }
  }
}
